import { cn } from '@/lib/utils'
import { ToastBar, Toast as ToastT, Toaster, toast } from 'react-hot-toast'
import { CheckCircle, XCircle, X, Info, AlertTriangle } from 'lucide-react'
import { ReactNode } from 'react'
import { WebrpcError } from '@/services/inference.gen'

export const API_TOAST_CLASS = '_api-toast_'

// Types for our notification component
type ToastType = 'success' | 'error' | 'info' | 'warning'

type ToastNotificationProps = {
  id: string
  visible: boolean
  type: ToastType
  title: string
  description: string
  suggestion?: string
  icon?: ReactNode
}

// Configuration for different toast types
const TOAST_CONFIGS = {
  success: {
    defaultIcon: <CheckCircle className="h-5 w-5" />,
    color: 'text-primary',
    bg: 'bg-primary/10',
    bar: 'bg-primary',
    border: 'border-primary/40 dark:border-primary/40',
  },
  error: {
    defaultIcon: <XCircle className="h-5 w-5" />,
    color: 'text-destructive',
    bg: 'bg-destructive/10',
    bar: 'bg-destructive',
    border: 'border-destructive/40 dark:border-destructive/40',
  },
  warning: {
    defaultIcon: <AlertTriangle className="h-5 w-5" />,
    color: 'text-accent',
    bg: 'bg-accent/10',
    bar: 'bg-accent',
    border: 'border-accent/40 dark:border-accent/40',
  },
  info: {
    defaultIcon: <Info className="h-5 w-5" />,
    color: 'text-secondary',
    bg: 'bg-secondary/10',
    bar: 'bg-secondary',
    border: 'border-secondary/40 dark:border-secondary/40',
  },
}

export const Toast = () => {
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 5000,
        style: {
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
        },
      }}
    >
      {(t) => <ToastAdapter t={t} />}
    </Toaster>
  )
}

const ToastAdapter = ({ t }: { t: ToastT }) => {
  switch (t.type) {
    case 'success':
      return (
        <ToastNotification
          id={t.id}
          visible={t.visible}
          type="success"
          title="Success"
          description={t.message as string}
        />
      )
    case 'error':
      try {
        const apiMessage = JSON.parse(t.message as string) as WebrpcError

        return (
          <ToastNotification
            id={t.id}
            visible={t.visible}
            type="error"
            title={apiMessage.message}
            description={apiMessage.cause || 'An error occurred'}
            suggestion={apiMessage.name || ''}
          />
        )
      } catch (error) {
        return (
          <ToastNotification
            id={t.id}
            visible={t.visible}
            type="error"
            title="Error"
            description={t.message as string}
          />
        )
      }
    default:
      // For other toast types, use the default ToastBar
      return (
        <ToastBar
          toast={{
            ...t,
            className: cn(
              t.className,
              'dark:bg-background dark:text-foreground/80 dark:border-2 dark:border-border'
            ),
          }}
        />
      )
  }
}

// The reusable notification component
const ToastNotification = ({
  id,
  visible,
  type = 'info',
  title,
  description,
  suggestion,
  icon,
}: ToastNotificationProps) => {
  const config = TOAST_CONFIGS[type] || TOAST_CONFIGS.info

  if (!visible) return null

  return (
    <div className="w-full max-w-sm relative">
      <div
        className={cn(
          'flex w-full rounded-md overflow-hidden',
          'backdrop-blur-lg shadow-lg shadow-black/5 border',
          'bg-background/90 dark:bg-background/90',
          config.border
        )}
      >
        {/* Left accent */}
        <div className={cn('w-1.5 shrink-0', config.bar)} />

        <div className="flex items-start p-3 gap-3 w-full">
          {/* Icon */}
          <div className={cn('mt-0.5 shrink-0', config.color)}>
            {icon || config.defaultIcon}
          </div>

          {/* Content */}
          <div className="flex-grow min-w-0">
            <h4 className="text-sm font-medium text-foreground flex items-center">
              {title}
            </h4>

            <p className="mt-1 text-sm text-foreground/80 break-words">
              {description}
            </p>

            {suggestion && (
              <p className="mt-1 text-xs text-foreground/60">{suggestion}</p>
            )}
          </div>

          {/* Close button */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              // Force close the toast directly
              toast.remove(id)
            }}
            className="shrink-0 p-1 rounded-full text-foreground/40 hover:text-foreground/60 
                    dark:hover:text-foreground/80 hover:bg-foreground/10 dark:hover:bg-foreground/20 
                    transition-colors self-start -mr-1 -mt-0.5 cursor-pointer"
          >
            <X className="h-4 w-4" />
          </div>
        </div>
      </div>
    </div>
  )
}
